import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "uikit";

import Order from "../../../../../../../../../../services/Order";
import PaymentLinkSmsModal, {
	isPaymentLinkSmsAwaiting,
} from "../../../../../../../../../../components/PaymentLinkSmsModal";
import { useTypedSelector } from "../../../../../../../../../../redux/store";
import OrderStatus from "../../../../../../../../../../types/OrderStatus";
import { AccessKey } from "../../../../../../../../../../access";
import Button from "../Button";

/**
 * Payment link by SMS for the selected order. Disabled while the sent link is
 * waiting for the payment; available again after a failed payment attempt or
 * a cancelled link. A paid order is a card one and cannot be sent either.
 * Whether the link can be issued at all (SMS link enabled in the payment
 * system etc.) is checked by the modal against the server.
 */
const PaymentLinkSmsButton: React.FC = () => {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState(false);

	const activeOrder = useTypedSelector(
		(state) => state.ordersPageReducer.activeOrder,
	);

	const hasExecutor = useMemo(
		() =>
			Boolean(
				activeOrder?.executorToOrder?.length ||
					activeOrder?.additionalFields?.transfer?.lastReceivedOrder
						?.executorToOrder?.length,
			),
		[activeOrder],
	);

	const isAwaiting = useMemo(
		() => isPaymentLinkSmsAwaiting(activeOrder),
		[activeOrder],
	);

	const canSend = useMemo(
		() =>
			Boolean(activeOrder?.id) &&
			!activeOrder?.closedAt &&
			activeOrder?.status === OrderStatus.EXECUTING &&
			hasExecutor &&
			activeOrder?.paymentType === Order.PaymentType.Cash &&
			!isAwaiting,
		[activeOrder, hasExecutor, isAwaiting],
	);

	const title = useMemo(
		() =>
			isAwaiting
				? t([
						"paymentLinkSms.awaitingPayment",
						"Payment link has been sent, awaiting payment",
				  ]) ?? ""
				: t(["paymentLinkSms.title", "Payment link by SMS"]) ?? "",
		[isAwaiting, t],
	);

	return (
		<>
			<Button
				disabled={!canSend}
				variant="secondary"
				title={title}
				tabIndex={-1}
				onClick={() => setIsOpen(true)}
				accessName={AccessKey.PAYMENT_LINK_SMS}
			>
				<Icon id="orders-payment-link-sms" size={20} />
			</Button>
			{isOpen && (
				<PaymentLinkSmsModal
					initialPhone={activeOrder?.phones?.[0]?.number}
					initialOrderNumber={activeOrder?.orderNumber}
					onClose={() => setIsOpen(false)}
				/>
			)}
		</>
	);
};

export default PaymentLinkSmsButton;
